import type { ReactNode } from 'react';
import { useAuth, useTranslation } from '@/contexts';
import { logoutWebAdmin } from '@/features/auth/logoutWebAdmin';
import styles from './StaffIdentityGate.module.css';

interface StaffIdentityGateProps {
  children: ReactNode;
}

/**
 * Blocks Admin shell until trusted staff identity is resolved and allowed.
 * Does not use a mock CEO as an entry grant.
 */
export function StaffIdentityGate({ children }: StaffIdentityGateProps) {
  const { identityStatus, canEnterAdminShell } = useAuth();
  const { t } = useTranslation();

  if (identityStatus === 'resolving' || identityStatus === 'idle') {
    return (
      <div className={styles.screen}>
        <p className={styles.text}>{t('auth.staffIdentityResolving')}</p>
      </div>
    );
  }

  if (identityStatus === 'denied' || !canEnterAdminShell) {
    return (
      <div className={styles.screen}>
        <div className={styles.card} role="alert">
          <h1 className={styles.title}>{t('auth.accessDenied.title')}</h1>
          <p className={styles.subtitle}>{t('auth.staffIdentityDenied')}</p>
          <button
            type="button"
            className={styles.signOutBtn}
            onClick={() => {
              void logoutWebAdmin();
            }}
          >
            {t('auth.signOut')}
          </button>
        </div>
      </div>
    );
  }

  return children;
}
